import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { TenantThemeProvider } from '@rallyshift/design-system';
import api from '../api/client';
import { getOrgSlug, isPlatformHost } from '../api/tenant';

export interface OrganizationBranding {
  id: string;
  name: string;
  slug: string;
  logo_url: string | null;
  brand_color: string;
  on_brand_color: string;
  locale: string;
  brand_contrast_ok: boolean;
  allow_public_signup: boolean;
  volunteer_check_in_enabled: boolean;
}

interface OrganizationContextValue {
  organization: OrganizationBranding | null;
  loading: boolean;
  /** Set when the host or slug does not resolve to an organisation. */
  error: string | null;
  refresh: () => Promise<void>;
}

const OrganizationContext = createContext<OrganizationContextValue | undefined>(undefined);

export function useOrganization(): OrganizationContextValue {
  const context = useContext(OrganizationContext);
  if (!context) throw new Error('useOrganization must be used within an OrganizationProvider');
  return context;
}

/**
 * Loads the branding for whichever organisation this host resolves to, before login,
 * and publishes it as the tenant theme. Volunteer surfaces are the organisation's;
 * only the RallyShift endorsement stays ours.
 */
export const OrganizationProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [organization, setOrganization] = useState<OrganizationBranding | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (isPlatformHost()) {
      setOrganization(null);
      setError(null);
      setLoading(false);
      return;
    }

    setLoading(true);
    try {
      setOrganization(await api.organizations.current());
      setError(null);
    } catch (e) {
      const slug = getOrgSlug();
      setOrganization(null);
      setError(
        slug
          ? `Organisatie "${slug}" bestaat niet.`
          : 'Deze omgeving hoort nog niet bij een organisatie.',
      );
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  // The static shell in index.html cannot know which tenant it is being served for, so
  // every organisation would otherwise inherit the last one baked into it.
  useEffect(() => {
    document.title = organization ? `${organization.name} — vrijwilligers` : 'RallyShift';
    document.documentElement.lang = organization?.locale || 'nl';

    const themeColor = document.querySelector('meta[name="theme-color"]');
    if (themeColor && organization?.brand_color) {
      themeColor.setAttribute('content', organization.brand_color);
    }
  }, [organization]);

  const value = useMemo(
    () => ({ organization, loading, error, refresh: load }),
    [organization, loading, error, load],
  );

  return (
    <OrganizationContext.Provider value={value}>
      <TenantThemeProvider organization={organization}>{children}</TenantThemeProvider>
    </OrganizationContext.Provider>
  );
};
